"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import { useParams } from "next/navigation";
import { PlyrVideoPlayer, type VideoProvider } from "@/components/video/plyr-video-player";

interface Props {
  videoUrl: string;
  provider?: VideoProvider;
  title?: string;
}

export function VideoProgressReporter({ videoUrl, provider, title }: Props) {
  const params = useParams();
  const courseSlug = params?.slug as string | undefined;
  const lessonSlug = params?.lesson as string | undefined;

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedProgressRef = useRef(0);
  const lastSavedMaxWatchedRef = useRef(0);
  const [initialMaxWatched, setInitialMaxWatched] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Load initial progress from the backend
  useEffect(() => {
    if (!courseSlug || !lessonSlug) return;

    const loadProgress = async () => {
      try {
        const response = await fetch(
          `/api/learn/courses/${courseSlug}/lessons/${lessonSlug}/progress`
        );
        if (response.ok) {
          const data = await response.json();
          setInitialMaxWatched(data.maxWatchedSeconds || 0);
          lastSavedMaxWatchedRef.current = data.maxWatchedSeconds || 0;
          lastSavedProgressRef.current = data.percentWatched || 0;
        }
      } catch (error) {
        console.error("Failed to load progress:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, [courseSlug, lessonSlug]);

  // Save progress to the backend
  const saveProgress = useCallback(
    async (percentWatched: number, maxWatchedSeconds: number, force = false) => {
      if (!courseSlug || !lessonSlug) return;

      // Don't save if progress hasn't changed significantly (avoid excessive API calls)
      // Unless it's a forced save (from periodic interval)
      if (!force) {
        const progressDiff = Math.abs(percentWatched - lastSavedProgressRef.current);
        const maxWatchedDiff = Math.abs(maxWatchedSeconds - lastSavedMaxWatchedRef.current);
        if (progressDiff < 0.01 && maxWatchedDiff < 1 && percentWatched < 0.75) {
          return;
        }
      }

      try {
        const response = await fetch(
          `/api/learn/courses/${courseSlug}/lessons/${lessonSlug}/progress`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              progressSeconds: 0,
              percentWatched,
              maxWatchedSeconds,
              completed: false, // Completion is handled separately via LessonProgressActions
            }),
          }
        );

        if (!response.ok) {
          throw new Error("progress_save_failed");
        }

        lastSavedProgressRef.current = percentWatched;
        lastSavedMaxWatchedRef.current = maxWatchedSeconds;
      } catch (error) {
        console.error("Failed to save progress:", error);
      }
    },
    [courseSlug, lessonSlug]
  );

  const currentProgressRef = useRef(0);
  const currentMaxWatchedRef = useRef(0);
  const periodicSaveIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Progress handler - receives progress as decimal between 0 and 1
  const handleProgress = useCallback(
    (progress: number) => {
      if (!courseSlug || !lessonSlug) return;
      currentProgressRef.current = progress;

      // Clear any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce saves by 3 seconds, but save faster if near completion threshold
      const delay = progress >= 0.7 ? 1000 : 3000;

      saveTimeoutRef.current = setTimeout(() => {
        saveProgress(currentProgressRef.current, currentMaxWatchedRef.current);
      }, delay);
    },
    [courseSlug, lessonSlug, saveProgress]
  );

  // Max watched seconds handler
  const handleMaxWatchedSecondsUpdate = useCallback((maxWatchedSeconds: number) => {
    currentMaxWatchedRef.current = maxWatchedSeconds;
  }, []);

  // Periodic save interval - save every 20 seconds regardless of progress updates
  useEffect(() => {
    if (!courseSlug || !lessonSlug) return;

    // Start periodic saves
    periodicSaveIntervalRef.current = setInterval(() => {
      if (currentProgressRef.current > 0) {
        // Force save even if progress hasn't changed much
        saveProgress(currentProgressRef.current, currentMaxWatchedRef.current, true);
      }
    }, 20000); // Save every 20 seconds

    return () => {
      if (periodicSaveIntervalRef.current) {
        clearInterval(periodicSaveIntervalRef.current);
      }
    };
  }, [courseSlug, lessonSlug, saveProgress]);

  // Save progress when component unmounts
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex aspect-video items-center justify-center rounded-lg bg-muted">
        <p className="text-muted-foreground">Loading video...</p>
      </div>
    );
  }

  return (
    <PlyrVideoPlayer
      videoUrl={videoUrl}
      provider={provider}
      title={title}
      autoPlay={false}
      onProgress={handleProgress}
      onMaxWatchedSecondsUpdate={handleMaxWatchedSecondsUpdate}
      completionThreshold={0.75}
      preventForwardSeek={true}
      initialMaxWatchedSeconds={initialMaxWatched}
    />
  );
}
